import { IHeTimesheet } from "@/types"
import { RibbonCard } from "@/components/ui/ribbon-card"
import { useNavigate } from "react-router"
import moment from "moment"
import { VariantProps } from "class-variance-authority"
import { useEffect, useRef } from "react"
import { Spin } from "antd"

interface Props {
  timesheets: IHeTimesheet[]
  onLoadMore?: () => void
  hasMore?: boolean
  isLoading?: boolean
}

const HeTimesheetCard = ({ timesheets, onLoadMore, hasMore = false, isLoading = false }: Props) => {
  const navigate = useNavigate()
  const observerTarget = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !loadingRef.current) {
          loadingRef.current = true
          onLoadMore?.()
        }
      },
      { threshold: 0.1 }
    )

    const currentObserverTarget = observerTarget.current

    if (currentObserverTarget) {
      observer.observe(currentObserverTarget)
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget)
      }
    }
  }, [hasMore, isLoading, onLoadMore])

  useEffect(() => {
    if (!isLoading) {
      loadingRef.current = false
    }
  }, [isLoading])

  const renderStatus = (status?: string): VariantProps<typeof RibbonCard>["variant"] => {
    switch (status) {
      case "DRAFT":
        return "warning"
      case "POSTED":
        return "success"
      case "LOCKED":
        return "error"
      default:
        return "black"
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="md:space-y-6 space-y-4">
        {timesheets?.map((timesheet, index) => (
          <RibbonCard
            key={`${timesheet.record_id}-${index}`}
            position="bottom-right"
            variant={renderStatus(timesheet.status)}
            ribbon={timesheet.status || "Unknown"}
          >
            <div className="flex flex-col justify-between md:gap-3 gap-2 cursor-pointer" onClick={() => navigate(`/hets/view/${timesheet.record_id}`)}>
              {/* Header */}
              <div className="flex flex-col">
                <p className="md:text-sm text-xs font-semibold">
                  {timesheet.hull_name} ({timesheet.hull_code})
                </p>
                <span className="md:text-xs text-[10px] font-medium">Record ID: {timesheet.record_id}</span>
              </div>

              {/* Key Info */}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">{timesheet.date_from && timesheet.date_to ? `${moment(timesheet.date_from).format("LLL")} - ${moment(timesheet.date_to).format("LLL")}` : "-"}</span>
              </div>
            </div>
          </RibbonCard>
        ))}
      </div>

      {/* Infinite Scroll Trigger */}
      <div ref={observerTarget} className="h-4 mt-4" />

      {isLoading && (
        <div className="text-center py-4">
          <Spin />
        </div>
      )}

      {!hasMore && timesheets.length > 0 && <div className="text-center py-4 text-gray-400 text-sm">No more items to load</div>}
    </div>
  )
}

export default HeTimesheetCard
